/** Small modal dialogs of the chat screen (rename conversation, upload prompt). */
import React from 'react';
import { Modal, TextInput, TouchableOpacity, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Text } from '@/components/ui/text';
import { useTranslation } from '../../../hooks/useTranslation';
import { COLORS } from '../chatConstants';

const backdrop = {
  flex: 1,
  justifyContent: 'center' as const,
  alignItems: 'center' as const,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  padding: 16,
};

export interface RenameSessionDialogProps {
  visible: boolean;
  title: string;
  onChangeTitle: (value: string) => void;
  onCancel: () => void;
  onSave: () => void;
}

export function RenameSessionDialog({ visible, title, onChangeTitle, onCancel, onSave }: RenameSessionDialogProps) {
  const { t } = useTranslation();
  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="pageSheet"
      transparent
      onRequestClose={onCancel}
    >
      <View style={backdrop}>
        <Card className="w-full max-w-sm bg-white">
          <CardHeader className="pb-4">
            <View className="flex-row items-center justify-between">
              <Text className="text-xl font-bold text-foreground">{t('chat.renameSession')}</Text>
              <TouchableOpacity onPress={onCancel} accessibilityRole="button" accessibilityLabel={t('common.close')}>
                <MaterialIcons name="close" size={24} color={COLORS.text.secondary} />
              </TouchableOpacity>
            </View>
          </CardHeader>
          <CardContent>
            <Text className="text-sm text-muted-foreground mb-3">{t('chat.enterNewTitle')}</Text>
            <TextInput
              style={{
                borderWidth: 1,
                borderColor: COLORS.border,
                borderRadius: 8,
                paddingHorizontal: 16,
                paddingVertical: 12,
                fontSize: 16,
                marginBottom: 16,
                backgroundColor: COLORS.surface,
              }}
              value={title}
              onChangeText={onChangeTitle}
              placeholder={t('chat.sessionTitlePlaceholder')}
              placeholderTextColor={COLORS.text.muted}
              maxLength={200}
              autoFocus
            />
            <View className="flex-row gap-3">
              <Button variant="secondary" size="default" onPress={onCancel} className="flex-1">
                <Text className="font-medium">{t('common.cancel')}</Text>
              </Button>
              <Button variant="default" size="default" onPress={onSave} className="flex-1">
                <Text className="text-primary-foreground font-semibold">{t('common.save')}</Text>
              </Button>
            </View>
          </CardContent>
        </Card>
      </View>
    </Modal>
  );
}

export interface UploadPromptDialogProps {
  visible: boolean;
  message: string;
  onCancel: () => void;
  onConfirm: () => void;
}

export function UploadPromptDialog({ visible, message, onCancel, onConfirm }: UploadPromptDialogProps) {
  const { t } = useTranslation();
  return (
    <Modal visible={visible} animationType="fade" transparent onRequestClose={onCancel}>
      <View style={backdrop}>
        <Card className="w-full max-w-sm bg-white">
          <CardHeader className="pb-3">
            <Text className="text-lg font-bold text-foreground">
              {t('chat.uploadTranscriptPromptTitle')}
            </Text>
          </CardHeader>
          <CardContent>
            <Text className="text-sm text-muted-foreground mb-4">
              {message || t('chat.uploadTranscriptPromptMessage')}
            </Text>
            <View className="flex-row gap-3">
              <Button variant="secondary" size="default" onPress={onCancel} className="flex-1">
                <Text className="font-medium">{t('common.cancel')}</Text>
              </Button>
              <Button variant="default" size="default" onPress={onConfirm} className="flex-1">
                <Text className="text-primary-foreground font-semibold">
                  {t('chat.uploadTranscriptPromptAction')}
                </Text>
              </Button>
            </View>
          </CardContent>
        </Card>
      </View>
    </Modal>
  );
}
